import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import ReactDOM from 'react-dom';
import { Dialog, DialogContent } from '@material-ui/core';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import { navigate } from '@gatsbyjs/reach-router/';
import { globalizationPath, useGlobal } from '@rcg/ui-react';
import { createCookie, cleanCookie, setSessionStorageItem, signOffProfile } from '../commons/utils';
import {
  COOKIE_KEYS,
  NAVIGATION_PATHS,
  SESSION_STORAGE_GUEST_ACCOUNT_CREATION,
  SESSION_EXPIRATION_MODAL_CONFIG,
} from '../commons/constants';
import { LITE_PROFILE } from '../graphql/queries';

const GlobalLoginContext = createContext(null);

export const useGlobalLogin = () => {
  const ctx = useContext(GlobalLoginContext);
  if (!ctx) throw new Error('useGlobalLogin must be used within GlobalLoginProvider');
  return ctx;
};

const SESSION_EXPIRATION_MODAL_CONFIG_JSON = JSON.stringify(SESSION_EXPIRATION_MODAL_CONFIG);

// TODO: Remove and adapt this when GA team expose logout functionality
function readSignInFormAuthService(element) {
  if (!element) return null;
  const signInForm =
    element.shadowRoot?.querySelector('sign-in-form') ?? element.querySelector('sign-in-form');
  if (!signInForm) return null;
  // eslint-disable-next-line no-underscore-dangle
  const instance = signInForm.__stencil__getHostRef?.().$lazyInstance$;
  return instance?.authService ?? null;
}

function resolveAuthenticationService(element) {
  if (!element) return null;
  // eslint-disable-next-line no-underscore-dangle
  const appRoot = element.__stencil__getHostRef?.().$lazyInstance$;
  if (!appRoot) return readSignInFormAuthService(element);

  if (appRoot.authSessionService?.authenticationService) {
    return appRoot.authSessionService.authenticationService;
  }

  try {
    const envConfig = appRoot.commonService?.constructor?.environment;
    if (envConfig && !appRoot.authSessionService) {
      appRoot.initializeSessionMonitoring(envConfig);
      if (appRoot.authSessionService?.authenticationService) {
        return appRoot.authSessionService.authenticationService;
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error initializing session monitoring during auth service resolution:', error);
  }

  return readSignInFormAuthService(element);
}

async function revokeAccessToken(element, capturedAuthService) {
  try {
    if (!element && !capturedAuthService) return;
    const liveAuthService = element ? resolveAuthenticationService(element) : null;
    const authService = liveAuthService ?? capturedAuthService;
    if (authService?.logout) {
      await authService.logout();
    } else {
      // eslint-disable-next-line no-console
      console.warn('Could not resolve auth service for token revocation');
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error revoking token via global-login:', error);
  }
}

// eslint-disable-next-line no-param-reassign
function resetSessionSingletons(instance) {
  const authService = instance.authSessionService;
  if (authService?.timeSessionMonitor) {
    const monitor = authService.timeSessionMonitor;
    monitor.destroy();
    monitor.constructor.instance = null;
    authService.timeSessionMonitor = null;
  }
  if (authService) {
    authService.constructor.instance = null;
    // eslint-disable-next-line no-param-reassign
    instance.authSessionService = null;
  }
}

function reinitializeSessionMonitoring(element) {
  try {
    if (!element) return;
    const hostRef = element.__stencil__getHostRef?.();
    const instance = hostRef?.$lazyInstance$;
    if (instance?.initializeSessionMonitoring && instance.commonService) {
      const envConfig =
        instance.commonService.constructor?.environment || instance.commonService.environment;
      if (envConfig) {
        resetSessionSingletons(instance);
        instance.initializeSessionMonitoring(envConfig);
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error reinitializing session monitoring:', error);
  }
}

function restartSessionMonitoring(element) {
  try {
    if (!element) return;
    const hostRef = element.__stencil__getHostRef?.();
    const instance = hostRef?.$lazyInstance$;
    if (!instance?.authSessionService) return;

    const envConfig =
      instance.commonService?.constructor?.environment || instance.commonService?.environment;
    if (!envConfig) return;

    resetSessionSingletons(instance);
    instance.initializeSessionMonitoring(envConfig);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error restarting session monitoring:', error);
  }
}

function resetSignInFormLoader(element) {
  try {
    if (!element) return;
    const signInForm = element.shadowRoot?.querySelector('sign-in-form');
    if (signInForm) {
      const hostRef = signInForm.__stencil__getHostRef?.();
      const instance = hostRef?.$lazyInstance$;
      if (instance) {
        instance.isLoading = false;
      }
      const primaryBtn = signInForm.shadowRoot?.querySelector('primary-button');
      const btn = primaryBtn?.shadowRoot?.querySelector('button');
      if (btn) {
        btn.removeAttribute('disabled');
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error resetting sign-in-form loader:', error);
  }
}

export const parkOffScreen = (container) => {
  Object.assign(container.style, {
    position: 'fixed',
    left: '-9999px',
    top: '-9999px',
  });
  document.body.appendChild(container);
};

export const clearInlinePosition = (container) => {
  Object.assign(container.style, { position: '', left: '', top: '' });
};

const dataLayer = {
  siteSection: 'myres',
  eventType: 'interaction',
  interaction: 'login:panel:opened',
  contentPosition: 'Header-GA-OrgranicSignIn',
  referringLinkLocation: 'guestaccount:myres:lookup',
};
const dataLayerJSON = JSON.stringify(dataLayer);

const globalLoginContainer = document.createElement('div');

const GlobalLoginProvider = ({ children }) => {
  const globalLoginElRef = useRef(null);
  const authServiceRef = useRef(null);
  const callbacksRef = useRef({});
  const [signInOpen, setSignInOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    Boolean(document.cookie.split('; ').find((c) => c.startsWith('accessToken='))),
  );
  const isAuthenticatedRef = useRef(isAuthenticated);
  const [externalDialogClaimed, setExternalDialogClaimed] = useState(false);

  const apolloClient = useApolloClient();

  const {
    data: { market, lang },
  } = useGlobal();

  const [dispatchLiteProfile, { data: rawLiteProfileData, loading: loadingLiteProfile }] =
    useLazyQuery(LITE_PROFILE, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    parkOffScreen(globalLoginContainer);
    return () => {
      if (globalLoginContainer.parentNode) {
        globalLoginContainer.parentNode.removeChild(globalLoginContainer);
      }
    };
  }, []);

  const captureAuthService = useCallback(async (el) => {
    if (!el) return;
    try {
      await el.componentOnReady?.();
      const signInForm =
        el.shadowRoot?.querySelector('sign-in-form') ?? el.querySelector('sign-in-form');
      if (!signInForm) return;
      await signInForm.componentOnReady?.();
      const authService = readSignInFormAuthService(el);
      if (authService) {
        authServiceRef.current = authService;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Could not capture AuthenticationService:', error);
    }
  }, []);

  useEffect(() => {
    captureAuthService(globalLoginElRef.current);
  }, [captureAuthService]);

  useEffect(() => {
    if (!rawLiteProfileData) return;
    const { creationDate = '', loyaltyData = {} } = rawLiteProfileData.getLiteProfile ?? {};
    if (creationDate) {
      setSessionStorageItem(SESSION_STORAGE_GUEST_ACCOUNT_CREATION, creationDate);
    }
    if (loyaltyData) {
      createCookie({ name: COOKIE_KEYS.LOYALTY_DATA, value: loyaltyData });
      callbacksRef.current.onLiteProfileLoaded?.({ creationDate, loyaltyData });
    }
  }, [rawLiteProfileData]);

  useEffect(() => {
    const el = globalLoginElRef.current;
    if (!el) return () => {};

    const onSuccess = (event) => {
      createCookie({
        name: COOKIE_KEYS.ACCESS_TOKEN,
        value: event.detail.accessToken,
        time: new Date(new Date().getTime() + 3.6e6),
      });
      createCookie({
        name: COOKIE_KEYS.VDS_ID,
        value: event.detail.vdsId,
      });

      const isFreshLogin = !isAuthenticatedRef.current;
      isAuthenticatedRef.current = true;
      setIsAuthenticated(true);
      if (isFreshLogin) {
        setTimeout(() => reinitializeSessionMonitoring(el), 0);
        captureAuthService(el);
        dispatchLiteProfile();
        setSignInOpen(false);
      }
      callbacksRef.current.onSuccess?.(event.detail);
    };

    const onError = (event) => {
      callbacksRef.current.onError?.(event.detail);
    };

    const onWarningDisplayed = (event) => {
      callbacksRef.current.onWarningDisplayed?.(event.detail);
    };

    const onSessionExtended = () => {
      setTimeout(() => restartSessionMonitoring(el), 0);
      callbacksRef.current.onSessionExtended?.();
    };

    const sessionCleanup = () => {
      signOffProfile(apolloClient);
      cleanCookie(COOKIE_KEYS.VDS_ID);
      isAuthenticatedRef.current = false;
      authServiceRef.current = null;
      setIsAuthenticated(false);
    };

    const onSessionExpired = () => {
      sessionCleanup();
      callbacksRef.current.onSessionExpired?.();
    };

    const onSessionNotExtended = () => {
      sessionCleanup();
      callbacksRef.current.onSessionNotExtended?.();
      navigate(globalizationPath(market, lang, NAVIGATION_PATHS[0].reservation));
    };

    const onSessionTimeoutModalRequestSignIn = () => {
      resetSignInFormLoader(el);
      setSignInOpen(true);
      callbacksRef.current.onSessionTimeoutModalRequestSignIn?.();
      navigate(globalizationPath(market, lang, NAVIGATION_PATHS[0].reservation));
    };

    el.addEventListener('successSession', onSuccess);
    el.addEventListener('errorComponent', onError);
    el.addEventListener('warningDisplayed', onWarningDisplayed);
    el.addEventListener('sessionExtended', onSessionExtended);
    el.addEventListener('sessionNotExtended', onSessionNotExtended);
    el.addEventListener('sessionExpired', onSessionExpired);
    el.addEventListener('sessionTimeoutModalRequestSignIn', onSessionTimeoutModalRequestSignIn);

    return () => {
      el.removeEventListener('successSession', onSuccess);
      el.removeEventListener('errorComponent', onError);
      el.removeEventListener('warningDisplayed', onWarningDisplayed);
      el.removeEventListener('sessionExtended', onSessionExtended);
      el.removeEventListener('sessionNotExtended', onSessionNotExtended);
      el.removeEventListener('sessionExpired', onSessionExpired);
      el.removeEventListener(
        'sessionTimeoutModalRequestSignIn',
        onSessionTimeoutModalRequestSignIn,
      );
    };
  }, [dispatchLiteProfile, apolloClient, captureAuthService]);

  const openSignIn = useCallback((callbacks = {}) => {
    callbacksRef.current = callbacks;
    resetSignInFormLoader(globalLoginElRef.current);
    setSignInOpen(true);
  }, []);

  const closeSignIn = useCallback(() => {
    setSignInOpen(false);
  }, []);

  const revokeToken = useCallback(
    () => revokeAccessToken(globalLoginElRef.current, authServiceRef.current),
    [],
  );
  const claimDialog = useCallback(() => {
    setExternalDialogClaimed(true);
  }, []);

  const releaseDialog = useCallback(() => {
    setExternalDialogClaimed(false);
  }, []);

  const showSessionDialog = signInOpen && !externalDialogClaimed;

  const sessionDialogContentRef = useCallback(
    (node) => {
      if (node && showSessionDialog && globalLoginContainer) {
        clearInlinePosition(globalLoginContainer);
        node.appendChild(globalLoginContainer);
      }
    },
    [showSessionDialog],
  );

  useEffect(() => {
    if (
      !showSessionDialog &&
      !externalDialogClaimed &&
      globalLoginContainer.parentNode !== document.body
    ) {
      parkOffScreen(globalLoginContainer);
    }
  }, [showSessionDialog, externalDialogClaimed]);

  const value = useMemo(
    () => ({
      signInOpen,
      openSignIn,
      closeSignIn,
      revokeToken,
      globalLoginContainer,
      loadingLiteProfile,
      claimDialog,
      releaseDialog,
      isAuthenticated,
    }),
    [
      signInOpen,
      openSignIn,
      closeSignIn,
      revokeToken,
      loadingLiteProfile,
      claimDialog,
      releaseDialog,
      isAuthenticated,
    ],
  );

  return (
    <GlobalLoginContext.Provider value={value}>
      {children}
      {ReactDOM.createPortal(
        <global-login
          ref={globalLoginElRef}
          brand="royal"
          env={process.env.REACT_APP_UNIFIED_PAYMENT_VERSION}
          enrollment
          language={lang}
          analytics-data-object={dataLayerJSON}
          session-expiration-modal-option={SESSION_EXPIRATION_MODAL_CONFIG_JSON}
        />,
        globalLoginContainer,
      )}
      <Dialog
        open={showSessionDialog}
        onClose={closeSignIn}
        scroll="body"
        closeAfterTransition
        maxWidth="md">
        <DialogContent dividers={false} ref={sessionDialogContentRef} />
      </Dialog>
    </GlobalLoginContext.Provider>
  );
};

export default GlobalLoginProvider;
